import PropTypes from '~/lib/propTypes'
import { Global, css } from '@emotion/react'
import useLogRecentlyVisitedEvents from '~/hooks/useLogRecentlyVisitedEvents'
import useDebounce from '~/hooks/useDebounce'
import Downshift, { resetIdCounter } from 'downshift'
import { device } from '@ticketswap/solar'
import { SEARCH_COMPLETED, SEARCH_STARTED, SEARCH_RESULT_CLICKED } from '~/lib/tracking-events'
import { track } from '~/utils/analytics'
import { getRouteLink } from '~/utils/getLinkProps'
import { decodeGlobalId } from '~/utils/id'
import { useLocaleBar } from '~/components/LocaleBarContext'
import { useState } from 'react'
import useLoggedInUser from '~/hooks/useLoggedInUser'
import SearchResults from '~/components/SiteSearch/SearchResults'
import { RouteMap } from '~/lib/routeMap'
import { useRouter } from 'next/router'
import { useGeoInfo } from '~/components/GeoInfoContext'

const SiteSearch = ({
  defaultOpen = false,
  onClose = () => null,
  trigger,
  width,
  resultsPerPage,
}) => {
  const router = useRouter()
  const noScrollClass = 'no-scroll'
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 500)
  const [filter, setFilter] = useState(null)
  const [recentlyVisitedEvents] = useLogRecentlyVisitedEvents()
  const { visible: visibleLocaleBar, hide: hideLocaleBar } = useLocaleBar()
  const { loggedInUser } = useLoggedInUser()
  const geoInfo = useGeoInfo()

  if (typeof window === 'undefined') {
    resetIdCounter()
  }

  const handleSearch = query => {
    if (typeof query !== 'undefined') {
      setQuery(query)
    }
  }

  const trackSearchResultsDebounced = hasResults => {
    track(SEARCH_COMPLETED, {
      hasResults,
      debouncedQuery,
    })
  }

  const stateReducer = (state, changes) => {
    // Prevent menu from closing when hiding the
    // keyboard in mobile Safari.
    if (changes.type === Downshift.stateChangeTypes.blurInput) {
      return {} // no changes
    }
    // Close menu on desktop when input loses focus,
    // but keep `inputValue` as is.
    if (changes.type === Downshift.stateChangeTypes.mouseUp) {
      return {
        ...changes,
        isOpen: false,
        inputValue: state.inputValue,
      }
    }
    // Close menu when clicking toggle button and
    // set `inputValue` to ''.
    if (changes.type === Downshift.stateChangeTypes.clickButton) {
      return {
        ...changes,
        isOpen: false,
        inputValue: '',
      }
    }
    return changes
  }

  const handleStateChange = ({ isOpen, inputValue }) => {
    if (isOpen) {
      if (visibleLocaleBar) {
        hideLocaleBar()
      }
    }

    // Perform search
    handleSearch(inputValue)
    // Toggle `.no-scroll` class on body
    if (typeof isOpen !== 'undefined') {
      let timer = null
      if (isOpen) {
        track(SEARCH_STARTED, {
          trigger,
        })
        if (!document.body.classList.contains(noScrollClass)) {
          document.body.classList.add(noScrollClass)
        }
        timer = setTimeout(() => {
          // Fixes an issue in mobile Safari where the input field
          // would be outside the visible viewport because of the
          // appearing keyboard. `60ms` may seem arbitrary, but felt
          // like the sweet spot when testing.
          if (!window.matchMedia(device.tablet).matches) {
            window.scrollTo(0, 0)
          }
        }, 60)
      } else {
        if (document.body.classList.contains(noScrollClass)) {
          document.body.classList.remove(noScrollClass)
        }
        clearTimeout(timer)
        // This is used to close the SearchOverlay wrapper
        onClose()
      }
    }
  }

  return (
    <Downshift
      id="site-search"
      onChange={selection => {
        if (!selection) return false
        const type = selection.__typename
        let handle = 'EVENT_PAGE'
        let params = {
          id: decodeGlobalId(selection.id),
          slug: selection.slug,
        }
        switch (type) {
          case 'CityResult':
            handle = 'CITY'
            break
          case 'LocationResult':
            handle = 'LOCATION'
            break
          case 'ArtistResult':
            handle = 'ARTIST'
            break
          default:
            params = {
              eventIdOrEventTypeSlug: decodeGlobalId(selection.id),
              eventSlug: selection.slug,
            }
            break
        }

        const link = getRouteLink(handle, params)

        track(SEARCH_RESULT_CLICKED, {
          contentId: decodeGlobalId(selection.id),
          contentName: selection.name,
          contentType: handle.split('_')[0],
          searchFilter: filter,
          link,
        })

        router.push({
          pathname: RouteMap[handle],
          query: params,
        })
      }}
      defaultOpen={defaultOpen}
      stateReducer={stateReducer}
      onStateChange={handleStateChange}
      itemToString={item => (item ? item.name : '')}
    >
      {({
        clearSelection,
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        getToggleButtonProps,
        highlightedIndex,
        inputValue,
        isOpen,
        openMenu,
        selectedItem,
      }) => {
        const withRecentlyVisited = Boolean(loggedInUser && inputValue.length === 0)

        return (
          <div>
            <SearchResults
              isOpen={isOpen}
              width={width}
              withRecentlyVisited={withRecentlyVisited}
              query={debouncedQuery}
              resultsPerPage={resultsPerPage}
              filter={filter}
              trackSearchResultsDebounced={trackSearchResultsDebounced}
              loggedInUser={loggedInUser}
              recentlyVisitedEvents={recentlyVisitedEvents}
              inputValue={inputValue}
              getToggleButtonProps={getToggleButtonProps}
              getInputProps={getInputProps}
              defaultOpen={defaultOpen}
              getLabelProps={getLabelProps}
              openMenu={openMenu}
              clearSelection={clearSelection}
              getMenuProps={getMenuProps}
              setFilter={setFilter}
              onClose={onClose}
              getItemProps={getItemProps}
              highlightedIndex={highlightedIndex}
              selectedItem={selectedItem}
              geoInfo={geoInfo}
            />
            <Global
              styles={css`
                body.no-scroll {
                  position: fixed;
                  inset: 0;

                  @media ${device.tablet} {
                    position: static;
                    inset: auto;
                  }
                }
              `}
            />
          </div>
        )
      }}
    </Downshift>
  )
}

SiteSearch.propTypes = {
  defaultOpen: PropTypes.bool,
  onClose: PropTypes.func,
  trigger: PropTypes.oneOf([
    'searchOverlay',
    'cart',
    'artistPage',
    'homepage',
    'ticketAlerts',
    'ticketsBought',
    'favoriteEvents',
  ]).isRequired,
  width: PropTypes.string,
  resultsPerPage: PropTypes.number,
}

export default SiteSearch
